import { colorToHex, isTransparent } from '../common/colors';
import type {
  BackgroundGradient,
  ExcalidrawElement,
  ShapeShadow,
} from './types';

export const supportsShapeEffects = (
  element: Pick<ExcalidrawElement, 'type'>
) =>
  element.type === 'rectangle' ||
  element.type === 'diamond' ||
  element.type === 'ellipse';

export const normalizeBackgroundGradient = (
  value: BackgroundGradient | null | undefined
): BackgroundGradient | null => {
  if (
    !value ||
    !Number.isFinite(value.angle) ||
    !Array.isArray(value.stops) ||
    value.stops.length < 2 ||
    value.stops.length > 16 ||
    value.stops.some(
      stop =>
        !stop ||
        typeof stop !== 'object' ||
        !Number.isFinite(stop.offset) ||
        stop.offset < 0 ||
        stop.offset > 1 ||
        typeof stop.color !== 'string' ||
        !colorToHex(stop.color)
    )
  )
    return null;
  return {
    angle: ((value.angle % 360) + 360) % 360,
    stops: value.stops
      .map(stop => ({ ...stop, color: colorToHex(stop.color)! }))
      .sort((a, b) => a.offset - b.offset),
  };
};
export const normalizeShapeShadow = (
  value: ShapeShadow | null | undefined
): ShapeShadow | null => {
  if (
    !value ||
    typeof value.color !== 'string' ||
    !colorToHex(value.color) ||
    ![value.blur, value.offsetX, value.offsetY].every(Number.isFinite) ||
    value.blur < 0 ||
    value.blur > 100 ||
    Math.abs(value.offsetX) > 200 ||
    Math.abs(value.offsetY) > 200
  )
    return null;
  return {
    color: colorToHex(value.color)!,
    blur: value.blur,
    offsetX: value.offsetX,
    offsetY: value.offsetY,
  };
};

export const getGradientEndpoints = (
  width: number,
  height: number,
  angle: number
) => {
  const radians = (angle * Math.PI) / 180;
  const dx = Math.cos(radians);
  const dy = Math.sin(radians);
  const length = Math.abs(width * dx) + Math.abs(height * dy);
  return {
    x1: (width - dx * length) / 2,
    y1: (height - dy * length) / 2,
    x2: (width + dx * length) / 2,
    y2: (height + dy * length) / 2,
  };
};

/** Conservative local/world padding; rotation never makes the offset exceed its length. */
export const getShapeShadowPadding = (element: ExcalidrawElement) =>
  supportsShapeEffects(element) && element.shadow
    ? Math.ceil(
        element.shadow.blur * 2 +
          Math.hypot(element.shadow.offsetX, element.shadow.offsetY) +
          element.strokeWidth
      )
    : 0;

export const isShapeBackgroundTransparent = (element: ExcalidrawElement) =>
  supportsShapeEffects(element) && element.backgroundGradient
    ? element.backgroundGradient.stops.every(stop => isTransparent(stop.color))
    : isTransparent(element.backgroundColor);
